import type { CrmCurrentPlanDto, TenantSummary } from '@/types/api';

/**
 * Determina qué IDs de empresa están permitidas por el plan.
 *
 * Prioridad de fuente de verdad:
 *  1. `crmPlan` (CRM externo, más preciso — incluye período de gracia).
 *  2. `activeTenant.planStatus` (ya disponible en el store persistido, sin esperar query CRM).
 *  3. Si no hay ninguno, asume FREE (restrictivo por defecto).
 *
 * - Plan FREE o EXPIRED: solo la primera empresa (por fecha de creación).
 * - Plan ACTIVE/SUSPENDED: las primeras N según planMaxCompanies.
 */
export function computeAllowedIds(
  companies:     { id: string; createdAt: string }[],
  crmPlan?:      CrmCurrentPlanDto  ,
  activeTenant?: Pick<TenantSummary, 'planStatus'>  ,
  isMaster?:     boolean,
): Set<string> {
  if (isMaster) return new Set(companies.map(c => c.id));

  const sorted = [...companies].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  // Fuente 1: plan CRM cargado (más preciso, considera período de gracia)
  if (crmPlan) {
    const count = (crmPlan.isExpired || crmPlan.isFreePlan)
      ? 1
      : (crmPlan.planMaxCompanies ?? 1);
    return new Set(sorted.slice(0, count).map(c => c.id));
  }

  // Fuente 2: planStatus del tenant (disponible desde el store persistido)
  if (activeTenant) {
    const isRestricted = activeTenant.planStatus === 'FREE' || activeTenant.planStatus === 'EXPIRED';
    if (isRestricted) {
      return new Set(sorted.slice(0, 1).map(c => c.id));
    }
    // ACTIVE o SUSPENDED sin plan CRM cargado → permitir todo hasta que cargue el detalle
    return new Set(sorted.map(c => c.id));
  }

  // Fuente 3: sin info de plan → restrictivo por defecto (1 empresa)
  return new Set(sorted.slice(0, 1).map(c => c.id));
}
